import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { Dialog, Button, DialogActions, DialogContent, DialogTitle, LinearProgress, FormControl, FormControlLabel, Checkbox, Typography, InputLabel, MenuItem, Select, Radio, RadioGroup } from "@material-ui/core";
import { ClubSelect, PlainTextField } from "components";
import { getMembers, patchMembersBulk, deleteMembersBulk } from "api/admin/members";
import i18n from "./i18n";

// prop types
interface MembersBulkEditDialogProps {
    showDialog: boolean,
    onClose: () => void,
    onRefreshMembersTable: () => void,
    isB2B?: boolean,
}

// admin members table bulk edit dialog
const MembersBulkEditDialog = ({ showDialog, onClose, onRefreshMembersTable, isB2B }: MembersBulkEditDialogProps) => {
    // user roles
    const userRoles = isB2B ? (["standard", "trainer", "admin"] as const) : (["standard", "groundskeeper", "trainer", "admin"] as const);
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    const VALID_SITE_LENGTH = 50;

    // ui state
    const [action, setAction] = useState<"edit" | "delete">("edit");
    const [affectedEntries, setAffectedEntries] = useState(0);
    const [clubId, setClubId] = useState<string | undefined>();
    const [isBlockedForBookings, setIsBlockedForBookings] = useState(false);
    const [userRole, setUserRole] = useState<typeof userRoles[number]>("standard");
    const [query, setQuery] = useState("");
    const [site, setSite] = useState("");
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);

    // update affected entries count
    useEffect(() => {
        getMembers({ clubId, query }).then(({ totalCount }) => setAffectedEntries(totalCount));
    }, [clubId, query]);

    // execute bulk action
    const executeAction = async () => {
        setShowLoadingAnimation(true);
        try {
            if (action === "edit") {
                await patchMembersBulk({ clubId, isBlockedForBookings, query, userRole, site });
            } else if (action === "delete") {
                await deleteMembersBulk({ clubId, query });
            }
            onRefreshMembersTable(); // fetch update data
            onClose();
        } catch (error) {
            displayError(error);
        }
        setShowLoadingAnimation(false);
    };

    // render
    return (
        <Dialog
            fullWidth
            open={showDialog}
            onClose={onClose}
        >
            <DialogTitle>{i18n.bulkEdit}</DialogTitle>
            <DialogContent dividers>

                {/* club */}
                <ClubSelect
                    clubId={clubId}
                    controlProps={{ fullWidth: true, margin: "normal" }}
                    userRoles={["admin", "owner"]}
                    onChange={setClubId}
                />

                {/* query */}
                <PlainTextField
                    label={i18n.query}
                    value={query}
                    variant="standard"
                    onChange={(e) => setQuery(e.target.value)}
                />

                {/* affected entries */}
                <Typography align="right" variant="subtitle2">
                    {i18n.affectedEntries(affectedEntries)}
                </Typography>

                {/* action */}
                <FormControl margin="normal">
                    <RadioGroup
                        row
                        value={action}
                        onChange={(e) => setAction(e.target.value as "edit" | "delete")}
                    >
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            label={i18n.edit}
                            value="edit"
                        />
                        <FormControlLabel
                            control={<Radio color="primary" />}
                            label={i18n.delete}
                            value="delete"
                        />
                    </RadioGroup>
                </FormControl>

                {/* edit fields */}
                {action === "edit" && (
                    <>
                        {/* user role */}
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="role_label">{i18n.status}</InputLabel>
                            <Select
                                labelId="role_label"
                                onChange={(e) => setUserRole(e.target.value as typeof userRole)}
                                value={userRole}
                            >
                                {userRoles.map((role) => (
                                    <MenuItem key={role} value={role}>{isB2B ? i18n.userRolesB2B[role] : i18n.userRolesB2C[role]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* site */}
                        <FormControl fullWidth margin="normal">
                            <PlainTextField
                                error={Number(site.length || 0) > VALID_SITE_LENGTH}
                                helperText={`${Number(site.length || 0)} / ${VALID_SITE_LENGTH}`}
                                inputProps={{ maxLength: VALID_SITE_LENGTH }}
                                label={i18n.site}
                                value={site}
                                variant="standard"
                                onChange={(e) => setSite(e.target.value)}
                            />
                        </FormControl>

                        {/* allow bookings */}
                        <FormControl margin="normal">
                            <FormControlLabel
                                control={(
                                    <Checkbox
                                        checked={!isBlockedForBookings}
                                        color="primary"
                                        onChange={(e) => setIsBlockedForBookings(!e.target.checked)}
                                    />
                                )}
                                label={i18n.allowBookings}
                            />
                        </FormControl>
                    </>
                )}

            </DialogContent>
            {showLoadingAnimation && (
                <LinearProgress />
            )}
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    {i18n.cancel}
                </Button>
                <Button onClick={executeAction} color="primary">
                    {i18n.execute}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MembersBulkEditDialog;
